import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Marquee from "@/components/Marquee";
import { KineticLines, Reveal } from "@/components/Reveal";
import { IMG } from "@/lib/images";
import { useSeo } from "@/lib/seo";
import { track } from "@/lib/track";

export default function OurStory() {
  useSeo({
    title: "Our Story | RIFIC — Real Tastes Better",
    description:
      "RIFIC is a premium Indian ice cream brand built on one belief: real tastes better. Discover the ingredient-first philosophy behind our scoops, shakes and desserts.",
    path: "/our-story",
  });

  return (
    <div data-testid="our-story-page">
      <PageHeader
        eyebrow="Our Story"
        title={["REAL TASTES", "BETTER."]}
        copy="RIFIC was born from a simple frustration: great fruit treated carelessly. So we set out to build an ice cream brand where the ingredient leads — and nothing gets in its way."
      />

      <section className="bg-cream pb-16 md:pb-24" data-testid="story-chapters">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <div className="overflow-hidden rounded-3xl bg-cream">
            <img
              src={IMG.storefront}
              alt="RIFIC 15 ft storefront — bold orange exterior with deep green branding"
              loading="lazy"
              className="h-auto w-full object-contain"
            />
          </div>

          <div className="mt-14 md:mt-20">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.no} delay={i * 0.05}>
                <div className="grid grid-cols-12 gap-3 border-t border-cream-line py-8 md:gap-6 md:py-12">
                  <span className="col-span-12 font-mono text-sm text-orange-rific md:col-span-1">{c.no}</span>
                  <h2 className="col-span-12 font-display text-2xl font-bold tracking-[-0.01em] text-deep md:col-span-5 md:text-4xl">
                    {c.title}
                  </h2>
                  <p className="col-span-12 text-base leading-relaxed text-deep/70 md:col-span-6 md:text-lg">
                    {c.copy}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee
        className="bg-deep py-4 text-cream md:py-5"
        textClassName="font-display text-xl md:text-3xl font-semibold uppercase tracking-wide"
        items={["TASTE HAS A SEASON.", "RIPE NOW.", "TASTE HAS A SEASON.", "RIPE NOW."]}
      />

      <section className="bg-deep py-16 md:py-24">
        <div className="mx-auto grid max-w-[1440px] items-center gap-10 px-5 md:px-10 lg:grid-cols-2">
          <div>
            <KineticLines
              lines={["FRESH. BOLD.", "PREMIUM. MODERN."]}
              className="block"
              lineClassName="font-display text-[clamp(2rem,4.2vw,3.25rem)] font-extrabold leading-[1.05] tracking-[-0.01em] text-cream"
            />
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-cream/75 md:text-lg">
                Indian-born, with international design standards. An ingredient-first brand that looks as good as it
                tastes — in the scoop, and on the street.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/menu"
                  data-testid="story-menu-cta"
                  className="inline-flex h-12 items-center gap-2 rounded-full bg-orange-rific px-7 text-xs font-bold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-cream hover:text-deep"
                >
                  Explore the Menu
                </Link>
                <Link
                  to="/franchise#franchise-form"
                  data-testid="story-franchise-cta"
                  onClick={() => track("franchise_cta_click", { placement: "our_story" })}
                  className="inline-flex h-12 items-center gap-2 rounded-full border border-cream/30 px-7 text-xs font-bold uppercase tracking-[0.16em] text-cream transition-colors duration-300 hover:border-orange-rific hover:text-orange-rific"
                >
                  Own a RIFIC
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-3xl bg-cream">
              <img
                src={IMG.storefrontPortrait}
                alt="RIFIC compact 10 ft storefront — rounded geometry, premium lighting"
                loading="lazy"
                className="h-auto w-full object-contain"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

const CHAPTERS = [
  {
    no: "01",
    title: "IT STARTS WITH THE FRUIT.",
    copy: "Mangoes that wait for summer. Strawberries that wait for winter. We build the menu around the fruit calendar — not the other way round.",
  },
  {
    no: "02",
    title: "THEN COMES THE MILK.",
    copy: "Creamy, honest milk as the base of everything we churn. Simple, familiar, non-negotiable.",
  },
  {
    no: "03",
    title: "THEN THE CRAFT.",
    copy: "Real fruit, real milk and real ingredients — churned into ice cream that lets the ingredient speak first.",
  },
];
